// Entities Service

import { apiClient, ApiClientError } from "@/lib/api/client";
import { API_CONFIG } from "@/lib/api/config";
import type { Entity, EntitiesResponse } from "@/types/api";
import { CONFIG } from "@/config";
import { inChunks } from "@/lib/inChunks";

// Batch reads return full entity rows, and Lambda caps a response at 6 MB.
// Keep each request small (iOS uses the same sizes) and merge client-side.
const MESSAGE_BATCH_CHUNK = 20;
const ENTITY_BATCH_CHUNK = 100;

// v2 entity types
export interface EntityType {
  type: string;
  count: number;
  description?: string;
}

export interface EntityTypesResponse {
  types: EntityType[];
  total: number;
}

export interface NearbyEntitiesParams {
  lat: number;
  lng: number;
  radius_km?: number;
  limit?: number;
  entity_type?: string;
  category?: string;
}

export interface EntitySearchResponse extends EntitiesResponse {
  query: string;
  took?: number;
}

export interface TrendingEntitiesResponse extends EntitiesResponse {
  period?: string;
}

export interface EntitiesBatchResponse {
  entities: Entity[];
  count?: number;
  requested?: number;
  found?: number;
  not_found?: string[];
}

export interface EntityPhotoManifestItem {
  index: number;
  url: string;
  availability: "cached" | "lazy";
  widthPx?: number;
  heightPx?: number;
  [key: string]: unknown;
}

export interface EntityPhotoManifestResponse {
  entity_id: string;
  photo_count: number;
  photos: EntityPhotoManifestItem[];
}

export interface MessageEntitiesResponse {
  stasht_id: string;
  entities: any[];
  count?: number;
  message?: string;
}

export interface EntitiesQueryParams {
  limit?: number;
  cursor?: string; // Use cursor instead of offset for pagination
  entity_type?: string; // Use entity_type instead of type
  sort?: "recent" | "name" | "type";
  q?: string; // search query
  category?: string; // READY: sender-category lookup via GSI
}

// Bbox entities types
export interface BboxEntitiesRequest {
  scope?: "mine" | "public";
  bbox: {
    min_lat: number;
    min_lng: number;
    max_lat: number;
    max_lng: number;
  };
  zoom?: number;
  limit?: number;
  entity_type?: string;
  category?: string;
  entity_category_key?: string;
  sort?: "distance" | "rating" | "recent";
}

export interface BboxEntitiesResponse {
  scope?: "mine" | "public";
  bbox: {
    min_lat: number;
    min_lng: number;
    max_lat: number;
    max_lng: number;
  };
  center: {
    lat: number;
    lng: number;
  };
  limit: number;
  total_matching: number;
  /** Raw search hit cap, before hidden or past-event filtering. */
  truncated: boolean;
  entities: EntitySummary[];
  entity_type_filter?: string;
  category_filter?: string;
  entity_category_key_filter?: string;
  user_overlay_matching?: number;
  sort: string;
}

// Entity summary — lightweight entity data returned by bbox endpoints
export interface EntitySummary {
  entity_id: string;
  name: string;
  routing_class: string;
  category?: string;
  entity_category_key?: string;
  google_place_id?: string;
  // Google Places photo name (e.g. "places/...")
  photo_uri?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  rating?: number;
  rating_count?: number;
  address?: string;
  // Opening hours as array of weekday descriptions (e.g. ["Monday: 11:00 AM – 4:00 PM", ...])
  opening_hours?: string[];
  distance_km?: number;
  discoverable_post_count?: number;
  is_stashed_by_me?: boolean;
  stashed_post_ids?: string[];
  stashed_post_count?: number;
  stashed_linked_at?: string;
  latest_discoverable_post_at?: string;
  calendar_enabled?: boolean;
  calendar_data?: {
    calendar_enabled?: boolean;
    dtstart_utc?: string;
    dtend_utc?: string;
    dtstart?: string;
    dtend?: string;
    dtstart_local?: string;
    dtend_local?: string;
    all_day?: boolean;
    [key: string]: unknown;
  };
  dtstart_utc?: string;
  dtend_utc?: string;
  dtstart?: string;
  dtend?: string;
  date?: string;
}

class EntitiesService {
  private static instance: EntitiesService;
  private get baseUrl(): string {
    return API_CONFIG.BASE_URL;
  }
  private entitiesCache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 60000; // 1 minute cache

  constructor() {
    console.log("🏗️ EntitiesService initialized with baseUrl:", this.baseUrl);
    console.log("📋 API_CONFIG details:", {
      BASE_URL: API_CONFIG.BASE_URL,
      API_KEY: API_CONFIG.API_KEY ? "Present" : "Missing",
      ENV_VALUE: CONFIG.API_URL,
    });
  }

  static getInstance(): EntitiesService {
    if (!EntitiesService.instance) {
      EntitiesService.instance = new EntitiesService();
    }
    return EntitiesService.instance;
  }

  // Get current configuration
  getConfig() {
    return {
      version: "v2",
      baseUrl: this.baseUrl,
    };
  }

  // Force reload with new version (for testing)
  switchVersion(useV2: boolean) {
    localStorage.setItem("ff_entities_v2", useV2.toString());
    window.location.reload();
  }

  // Clear cache
  clearCache() {
    this.entitiesCache.clear();
  }

  // Private helper for API calls - SAME AUTH AS EVENTS V2 THAT WORKS!
  private async apiCall<T>(
    endpoint: string,
    options: { method?: "GET" | "POST" | "PUT" | "DELETE"; body?: any; skipAuth?: boolean } = {},
  ): Promise<T> {
    const method = (options.method ?? "GET").toUpperCase() as "GET" | "POST" | "PUT" | "DELETE";
    const skip = options.skipAuth ?? false;
    const cacheKey = `${endpoint}:${method}:${options.body ? JSON.stringify(options.body) : ""}`;
    const cached = this.entitiesCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION && method === "GET") {
      return cached.data as T;
    }

    const requestByMethod: Record<"GET" | "POST" | "PUT" | "DELETE", () => Promise<T>> = {
      GET: () => apiClient.get<T>(endpoint, skip),
      POST: () => apiClient.post<T>(endpoint, options.body, skip),
      PUT: () => apiClient.put<T>(endpoint, options.body, skip),
      DELETE: () => apiClient.delete<T>(endpoint, skip),
    };

    const request = requestByMethod[method];
    if (!request) {
      throw new Error(`Unsupported method: ${method}`);
    }

    const data = await request();
    if (method === "GET") {
      this.entitiesCache.set(cacheKey, { data, timestamp: Date.now() });
    }
    return data;
  }

  // Core method - get entities list with proper pagination
  async getEntities(params: EntitiesQueryParams = {}): Promise<EntitiesResponse> {
    const queryParams = new URLSearchParams();

    // Use proper parameter names per API docs
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.entity_type) queryParams.append("entity_type", params.entity_type);
    if (params.sort) queryParams.append("sort", params.sort);
    if (params.q) queryParams.append("q", params.q);

    // READY: sender-category lookup via GSI
    if (params.category) {
      queryParams.append("category", params.category);
      console.log("🏷️ Adding category parameter for sender-category GSI lookup:", params.category);
    }

    const endpoint = `/entities${queryParams.toString() ? "?" + queryParams : ""}`;

    console.log("🔍 Entities API call with params:", {
      endpoint,
      params: Object.fromEntries(queryParams),
      categoryLookup: !!params.category,
    });

    return this.apiCall<EntitiesResponse>(endpoint);
  }

  async getNearbyEntities(params: NearbyEntitiesParams): Promise<EntitiesResponse> {
    if (params.lat === undefined || params.lng === undefined) {
      throw new Error("Latitude and longitude are required");
    }

    const queryParams = new URLSearchParams();
    queryParams.append("lat", params.lat.toString());
    queryParams.append("lng", params.lng.toString());

    if (params.radius_km) {
      const radius = Math.min(Math.max(params.radius_km, 0.1), 5000);
      queryParams.append("radius_km", radius.toString());
    }

    if (params.limit) {
      queryParams.append("limit", params.limit.toString());
    }

    if (params.entity_type) {
      queryParams.append("entity_type", params.entity_type);
    }

    if (params.category) {
      queryParams.append("category", params.category);
    }

    const endpoint = `/entities/nearby?${queryParams.toString()}`;

    console.log("📍 Nearby entities request:", {
      endpoint,
      params: Object.fromEntries(queryParams),
    });

    return this.apiCall<EntitiesResponse>(endpoint);
  }

  // Get single entity - FIXED for backend API format
  // skipAuth allows anonymous access (backend returns entity without user-specific fields)
  async getEntity(entityId: string, skipAuth = false): Promise<any | null> {
    try {
      console.log("🎯 Fetching individual entity:", entityId);
      const endpoint = `/entities/${entityId}`;

      // Backend returns entity data directly, not wrapped in { entity: ... }
      // Return as 'any' to preserve all fields including stasht_ids
      const response = await this.apiCall<any>(endpoint, { skipAuth });

      console.log("✅ Individual entity response:", response);
      return response;
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 404) {
        return null;
      }
      console.error("❌ Failed to fetch individual entity:", error);
      throw error;
    }
  }

  async getEntityPhotoManifest(
    entityId: string,
    maxPhotos = 10,
  ): Promise<EntityPhotoManifestResponse> {
    if (!entityId) {
      throw new Error("Entity ID is required");
    }

    return this.apiCall<EntityPhotoManifestResponse>(
      `/entities/${encodeURIComponent(entityId)}/photos`,
      {
        method: "POST",
        body: {
          intent: "gallery_view",
          max_photos: Math.min(Math.max(Math.trunc(maxPhotos), 1), 10),
        },
        // Gallery manifests are intentionally available on public entity pages.
        skipAuth: true,
      },
    );
  }

  async getEntitiesForMessage(stashtId: string, skipAuth = false): Promise<MessageEntitiesResponse> {
    if (!stashtId) {
      throw new Error("Stasht ID is required");
    }

    return this.apiCall<MessageEntitiesResponse>(`/entities/message/${stashtId}`, {
      method: "POST",
      body: {},
      skipAuth,
    });
  }

  async getEntitiesForMessageBatch(
    stashIds: string[],
  ): Promise<{ results: Record<string, any[]>; total_entities: number }> {
    if (!stashIds.length) return { results: {}, total_entities: 0 };
    const pages = await inChunks(stashIds, MESSAGE_BATCH_CHUNK, (chunk) =>
      this.apiCall<{ results: Record<string, any[]>; total_entities: number }>(
        "/entities/message/batch",
        { method: "POST", body: { stash_ids: chunk } },
      ),
    );
    return {
      results: Object.assign({}, ...pages.map((page) => page.results ?? {})),
      total_entities: pages.reduce((sum, page) => sum + (page.total_entities ?? 0), 0),
    };
  }

  async deleteEntity(entityId: string): Promise<Record<string, unknown> | null> {
    if (!entityId) {
      throw new Error("Entity ID is required");
    }

    const result = await apiClient.delete<Record<string, unknown> | null>(`/entities/${entityId}`);
    this.entitiesCache.clear();
    return result;
  }

  async getEntitiesBatch(
    entityIds: string[],
    options: { allowUnlinked?: boolean; skipAuth?: boolean } = {},
  ): Promise<EntitiesBatchResponse> {
    if (!entityIds || entityIds.length === 0) {
      return { entities: [], count: 0 };
    }

    const pages = await inChunks(entityIds, ENTITY_BATCH_CHUNK, (chunk) =>
      this.apiCall<EntitiesBatchResponse>("/entities/batch", {
        method: "POST",
        body: {
          ids: chunk,
          allow_unlinked: options.allowUnlinked ?? false,
        },
        skipAuth: options.skipAuth,
      }),
    );
    if (pages.length === 1) return pages[0];
    const entities = pages.flatMap((page) => page.entities ?? []);
    return {
      entities,
      count: entities.length,
      requested: entityIds.length,
      found: entities.length,
      not_found: pages.flatMap((page) => page.not_found ?? []),
    };
  }

  // Get entity types
  async getEntityTypes(): Promise<EntityTypesResponse> {
    try {
      return await this.apiCall<EntityTypesResponse>("/entities/types");
    } catch (error) {
      console.error("Failed to fetch entity types:", error);
      return { types: [], total: 0 };
    }
  }

  // Search entities
  async searchEntities(query: string, params: EntitiesQueryParams = {}): Promise<EntitySearchResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append("q", query);
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.cursor) queryParams.append("cursor", params.cursor);
    if (params.entity_type) queryParams.append("entity_type", params.entity_type);
    if (params.sort) queryParams.append("sort", params.sort);

    const endpoint = `/entities/search?${queryParams}`;

    return this.apiCall<EntitySearchResponse>(endpoint);
  }

  // Get trending entities
  async getTrendingEntities(limit: number = 10): Promise<TrendingEntitiesResponse> {
    const endpoint = `/entities/trending?limit=${limit}`;
    return this.apiCall<TrendingEntitiesResponse>(endpoint);
  }

  // Fetch entities for a bounding box using /entities/bbox endpoint
  async getBboxEntities(params: BboxEntitiesRequest): Promise<BboxEntitiesResponse> {
    const body: BboxEntitiesRequest = {
      scope: params.scope,
      bbox: params.bbox,
      zoom: params.zoom,
      limit: params.limit || 5000,
      entity_type: params.entity_type,
      category: params.category,
      entity_category_key: params.entity_category_key,
      sort: params.sort || "distance",
    };

    console.log("[bbox] Request", body);
    const data = await this.apiCall<BboxEntitiesResponse>("/entities/bbox", { method: "POST", body });
    console.log("[bbox] Response:", data.entities?.length || 0, "entities");
    return data;
  }
}

// Export singleton instance
export const entitiesService = EntitiesService.getInstance();

// Also export class for testing
export default EntitiesService;
